import {Control} from 'fabric';
import * as fabric from 'fabric';

const {rotationWithSnapping} = fabric.controlsUtils;

declare global {
    interface Window {
        primaryColor: string;
        secondaryColor: string;
        surfaceColor: string;
    }
}

const CUSTOM_ROTATE_FLAG = '__customRotateIcon';

let cachedRotateIcon: HTMLImageElement | null = null;
let cachedRotateIconColor: string | null = null;

const ROTATE_ICON_PATH =
    'M15.25 18.48V15a.75.75 0 1 0-1.5 0v4c0 .97.78 1.75 1.75 1.75h4a.75.75 0 1 0 0-1.5h-2.6a8.75 8.75 0 0 0-2.07-15.53.75.75 0 1 0-.49 1.42 7.25 7.25 0 0 1 .91 13.34zM8.75 5.52V9a.75.75 0 0 0 1.5 0V5c0-.97-.78-1.75-1.75-1.75h-4a.75.75 0 0 0 0 1.5h2.6a8.75 8.75 0 0 0 2.18 15.57a.75.75 0 0 0 .47-1.43 7.25 7.25 0 0 1-1-13.37z';

const normalizeColor = (color: string): string => {
    if (typeof color !== 'string' || color.trim().length === 0) {
        return '#4f46e5';
    }

    return color.trim();
};

const buildRotateIconSrc = (color: string): string => {
    const safeColor = normalizeColor(color);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="${safeColor}" d="${ROTATE_ICON_PATH}"/></svg>`;

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const getRotateIcon = (color: string): HTMLImageElement => {
    const safeColor = normalizeColor(color);

    if (cachedRotateIcon && cachedRotateIconColor === safeColor) {
        return cachedRotateIcon;
    }

    const icon = new Image();
    icon.src = buildRotateIconSrc(safeColor);
    cachedRotateIcon = icon;
    cachedRotateIconColor = safeColor;

    return icon;
};

const hasCustomRotateControl = (obj: any): boolean =>
    obj?.controls?.mtr?.[CUSTOM_ROTATE_FLAG] === true;

const drawRotateIconFallback = (
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    color: string,
): void => {
    const size = 24;
    ctx.save();
    ctx.translate(left, top);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, size / 2 - 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, size / 4, 0.4 * Math.PI, 1.6 * Math.PI);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-2, -5);
    ctx.lineTo(-6, -5);
    ctx.lineTo(-4, -9);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.restore();
};

const applyRotateControl = (obj: any, rotateIcon: HTMLImageElement, color: string): void => {
    const assignControl = () => {
        const control = new Control({
            x: 0,
            y: -0.5,
            actionHandler: rotationWithSnapping,
            cursorStyle: 'grab',
            offsetY: -40,
            withConnection: true,
            actionName: 'rotate',
            cornerSize: 24,
            render(ctx: CanvasRenderingContext2D, left: number, top: number) {
                const size = 24;

                ctx.save();

                if (rotateIcon.complete && rotateIcon.naturalWidth > 0) {
                    ctx.drawImage(rotateIcon, left - size / 2, top - size / 2, size, size);
                } else {
                    drawRotateIconFallback(ctx, left, top, color);
                }

                ctx.restore();
            },
        });

        (control as any)[CUSTOM_ROTATE_FLAG] = true;
        obj.controls.mtr = control;
        obj.setCoords();
        obj.canvas?.requestRenderAll();
    };

    if (rotateIcon.complete && rotateIcon.naturalWidth > 0) {
        assignControl();
        return;
    }

    rotateIcon.addEventListener('load', assignControl, {once: true});
    assignControl();
};

export const setRenderProperties = (obj: any): void => {
    if (!obj) {
        return;
    }

    const primaryColor = normalizeColor(window.primaryColor || '#4f46e5');

    obj.set('borderColor', primaryColor);
    obj.set('cornerColor', 'white');
    obj.set('cornerStrokeColor', primaryColor);
    obj.set('cornerSize', 10);
    obj.set('transparentCorners', false);
    obj.set('cornerStyle', 'circle');
    obj.set('centeredRotation', true);
    obj.set('snapAngle', 5);
    obj.set('hasRotatingPoint', true);
    obj.set('lockRotation', false);

    if (hasCustomRotateControl(obj)) {
        return;
    }

    applyRotateControl(obj, getRotateIcon(primaryColor), primaryColor);
};